import { DateFormat } from "./DateFormat";
import { TimeFormat } from "./TimeFormat";

/**
 * Class provides methods for formatting date and time to a selected format.
 *
 * All date formats are listed in @see {@link DateFormat.ts} and time formats in @see {@link ~TimeFormat.ts}.
 *
 * @author michaelp
 */
export class DTFormatter {

    /**
     * Method converts the string representation of a given date stamp into a Date() object,
     * then exctracts the date part and formats it to a selected locale (format) and returns a string representation.
     *
     * @param dateString The string representation of a date in Unix format.
     * @param dateFormat The date format (optional). Defaults to Unix date format.
     * @returns The string representation of a given date in a selected format.
     */
    public static getFormattedDate(dateString: string, dateFormat: string = DateFormat.Unix) {
        let date = new Date(dateString);

        var formattedDate = date.toLocaleDateString();
        
        // when calling getMonth, January is 0
        let month = date.getMonth() +1;
        let day = date.getDate();
        let year = date.getFullYear();

        switch (dateFormat) {
            case DateFormat.Unix:
                formattedDate = `${year}-${DTFormatter.padNumber(month)}-${DTFormatter.padNumber(day)}`;
            break;

            case DateFormat.American:
                formattedDate = `${month}/${day}/${year}`;
            break;

            case DateFormat.European:
                formattedDate = `${day}/${month}/${year}`;
            break;
        }

        return formattedDate;
        
    }

    /**
     * Method converts the string representation of a given datetime stamp into a Date() object
     * then exctracts the time part and formats it to a selected locale (format) and returns a string representation.
     *
     * @param dateTimeString The string representation of a datetime stamp.
     * @param timeFormat The time format (optional). Defaults to 12-Hour time format.
     * @returns The string representation of a given timestamp in a selected format.
     */
    public static getFormattedTime(dateTimeString: string,  timeFormat: string = TimeFormat.AmPm) {
        let date = new Date(dateTimeString);
        let is24hTimeEnabled = timeFormat === TimeFormat.Military;

        let formattedTime = "";

        let hours = date.getHours();
        let minutes = DTFormatter.padNumber(date.getMinutes());
        let seconds = DTFormatter.padNumber(date.getSeconds()); 
        let amPm = (hours >= 12) ? "PM" : "AM";

        if (is24hTimeEnabled) {
            formattedTime = `${DTFormatter.padNumber(hours)}:${minutes}:${seconds}`;
        } else {
            var formattedHours = hours;
            if (hours > 12) {
                formattedHours = hours - 12;
            } else if (hours == 0) {
                formattedHours = 12;
            }
            formattedTime = `${formattedHours}:${minutes}:${seconds} ${amPm}`;
        }

        return formattedTime;
    }

    /**
     * Method converts the string representation of a given datetime stamp into a Date() object
     * then formats it to a selected locale (format) and returns a string representation.
     * 
     * @param dateTimeString The string representation of a datetime stamp.
     * @param dateFormat The date format (optional). Defaults to Unix date format.
     * @param timeFormat The time format (optional). Defaults to 12-Hour time format.
     * @returns The string representation of a given datetime stamp in a selected format.
     */
    public static getFormattedDateTime(dateTimeString: string, dateFormat: string = DateFormat.Unix, timeFormat: string = TimeFormat.AmPm) {
        let formattedDate = DTFormatter.getFormattedDate(dateTimeString, dateFormat);
        let formattedTime = DTFormatter.getFormattedTime(dateTimeString, timeFormat);

        return `${formattedDate} ${formattedTime}`;
    }

    private static padNumber(number: number) {
        return number < 10 ? `0${number}` : number;
    }
}